import commonMixin from './../mixins/_common';
import flashMessageMixin from './../mixins/_flash_messages';
import modalMixin from './../mixins/_modal';

// 一括でUPできる最大画像容量100M(413エラーが解消されない為、フロントでは30M制限)
const MAX_IMAGES_SIZE = 30000000;

/**
 * 複数画像UPロードコンポーネント
 */
export default {
    name: 'MultiImageUploader',
    template: '#multi_image_uploader_template',
    data: function () {
        return {
            // D&D中フラグ
            dragging: false,
            // inputタグを表示フラグ(再描画のために使う)
            show_input: true,
            // アップロード画像URL
            image_url_text: '',
            // 一意ID
            uniqueId: null,
        };
    },
    props: {
        // 現在の設定中画像データ
        modelValue: {
            type: Array,
            required: true,
            default: []
        },
        // 画像の保存先
        page_dir: {
            type: String,
            required: false,
            default: 'common',
            validator: function (value) {
                // プロパティの値は、必ずいずれかの文字列でなければならない
                return ['build_store', 'item', 'custom_page'].indexOf(value) !== -1;
            }
        },
        // 保存する画像の接頭語
        edit_id_dir: {
            type: [String, Number],
            required: false,
            default: null
        },
        // 最大登録枚数(指定なしは0)
        max_image_num: {
            type: [Number, Boolean],
            required: false,
            default: 0
        }
    },
    mixins: [
        commonMixin,
        modalMixin,
        flashMessageMixin,
    ],
    computed: {
        // 最大登録枚数の上限があるか
        isMaxImageCount: function () {
            return this.max_image_num > 0;
        },
        // 最大追加画像枚数(上限なしならfalse)
        maxImageCount: function () {
            return this.isMaxImageCount ? this.max_image_num - this.modelValue.length : false;
        },
        // あと何枚追加可能か
        maxImageCountRealtime: function () {
            return this.isMaxImageCount ? this.maxImageCount - this.image_urls.length : false;
        },
        // 一括でUPできる最大画像容量
        maxImagesSize: function () {
            return MAX_IMAGES_SIZE;
        },
        // 最大画像容量を超えた時に表示するメッセージ
        overMaxImagesSizeMessage: function () {
            return ['一度にUPロードできる容量は' + this.$_formatBytes(this.maxImagesSize, 1000) + 'までです。分割してアップロードをお願いします。'];
        },
        // 最大追加画像枚数を超えた時に表示するメッセージ
        maxImageCountMessage: function () {
            return `残り追加可能な枚数 ${this.maxImageCount}枚を超えています。`;
        },
        // 商品画像URL配列
        image_urls: function () {
            return this.image_url_text
                .split(/\r?\n/)
                .filter(line => line.trim() !== '')
                .map(url => url.split('?')[0]); // URLからクエリパラメータを除去
        },
        // コンポーネント毎の一意のキー
        // このコンポーネントが複数個呼ばれている可能性があるため一意にする
        componentId: function () {
            if (!this.uniqueId) {
                this.uniqueId = 'multi_image_uploader_' + Date.now();
            }
            return this.uniqueId;
        },
        idImageFile: function () {
            return this.componentId + '_image_file';
        },
        idImageUrl: function () {
            return this.componentId + '_image_url';
        },
    },
    emits: ['update:modelValue'],
    methods: {
        // inputタグを初期化
        resetInput: async function () {
            this.show_input = false;
            await this.$nextTick();
            this.show_input = true;
        },
        // -------------------------
        // サーバへの画像アップロード
        // -------------------------
        /**
         *  ファイルアップロードによる画像登録を行う
         */
        setImagesByUploading: async function (e) {
            // 初期化
            this.clearFlashMessage(null, this.$root);
            this.dragging = false;

            // 送信データ
            const form = new FormData();
            form.append('edit_id_dir', this.edit_id_dir);
            form.append('page_dir', this.page_dir);

            let files = await this.getFiles(e);
            let message = this.validateFiles(files, form);

            // アップロード処理
            if (message.length === 0 && form.has('files[]')) {
                await this.uploadFiles(form);
            }
            // エラーメッセージあり
            if (message.length) {
                console.log('エラーメッセージあり');
                this.appendFlashMessage({ level: 'alert-danger', message }, this.$root);
            }
        },
        /**
         *  URLによる画像登録を行う
         */
        setImagesByUrl: function () {

            this.clearFlashMessage(null, this.$root);
            const errorMessages = [];

            this.image_urls.every(imageUrl => {
                const imageUrlRegex = /^https:\/\/shopping\.c\.yimg\.jp\/lib\/.+$|^https:\/\/item-shopping\.c\.yimg.jp\/i\/[a-zA-Z]\/.+$/;
                if(!imageUrlRegex.test(imageUrl)) {
                    errorMessages.push('無効なURLが入力されています');
                    return false;
                }
                return true;
            });

            // 上限枚数指定がある場合は枚数チェック
            if (this.isMaxImageCount && this.maxImageCountRealtime < 0) {
                errorMessages.push(this.maxImageCountMessage);
            }

            if (errorMessages.length) {
                this.appendFlashMessage({ level: 'alert-danger', message: errorMessages }, this.$root);
                return;
            }

            // 親に通知
            this.emitUpdateValue(this.image_urls);
            // 初期化
            this.image_url_text = '';
            this.close();
        },
        /**
         * ファイル取得
         */
        getFiles: async function (e) {
            await this.resetInput();
            return e.dataTransfer ? e.dataTransfer.files : e.target.files;
        },
        /**
         * ファイル検証とフォームデータ構築
         *
         * @param $files
         * @param $form
         */
        validateFiles: function (files, form) {
            let message = [];
            let filesSize = 0;

            if (files.length === 0) {
                message.push('アップロードに失敗しました');
            } else if (this.isMaxImageCount && files.length > this.maxImageCount) {
                message.push(this.maxImageCountMessage);
            } else {
                for (const file of files) {
                    if (!/^image\/.*$/.test(file.type)) {
                        message.push(`「${file.name}」は画像ではありません`);
                    } else {
                        filesSize += file.size;
                        form.append('files[]', file);
                    }
                }
                if (filesSize > this.maxImagesSize) {
                    form.delete('files[]');
                    message = this.overMaxImagesSizeMessage;
                }
            }

            return message;
        },
        /**
         * ファイルアップロード処理
         */
        uploadFiles: async function (form) {
            this.$_loading();

            try {
                const response = await axios.post('/upload_image', form);
                if (response.data.status === 'OK') {
                    this.emitUpdateValue(response.data.data.file_names);
                    this.close();
                }
            } catch (e) {
                let message = e.response.status === 413 ? this.overMaxImagesSizeMessage : this.getCatchMessage(e);
                this.appendFlashMessage({ level: 'alert-danger', message }, this.$root);
            } finally {
                this.$_unloading();
            }
        },

        // -------------------------
        // emit
        // -------------------------

        // 親へ通知
        emitUpdateValue: function (fileNames) {
            // 現状の画像リストに追加
            let images = _.cloneDeep(this.modelValue);
            for (const i in fileNames) {
                images.push({image: fileNames[i]});
            }
            this.$emit('update:modelValue', images);
        }
    }
};
